// index.ts — entry point for the list-display bundle (IIFE → includes/js/dist/
// list-display-bundle.js). Exposes window.renderItemListDisplay, the client-side
// port of the read-display rows in options/list_display/__list_display_combined_inc.php.
//
// Fed by options/tools/encounter_creatures/get_item_arr.php:
//   { item_arr, _ft_referenced_names }
// Render-time helpers (iconMap / escapeHtml / ucwordsAndSpace / checkIfValidUUID / BASE_URL) come
// from includes/js/core-helpers.js — the shared-primitives module loaded FIRST in the footer
// (before this bundle AND before template.js). That single source of truth replaced the duplicate
// copies this bundle used to ship in ./bundle-helpers (removed 2026-06-01, Phase 1 of
// plans/template-js-carve-up.md): core-helpers loads before us, so the globals are installed by the
// time we paint — no 779KB template.js wait → no first-paint flash. The inlined ICON_LOOKUP /
// *_CATALOG consts (template_page_footer.php) are read as-is (defined before us).

import { buildItemListDisplayHtml, buildCreatureSummonsListsHtml, buildCreatureLairListsHtml, buildCreatureMountListsHtml, buildCreatureInventoryHtml, buildCreatureHarvestYieldsHtml } from './render-item-list-display';
import { ftHydrateUserChoiceOptions } from './user-choice-hydrate';
import { mountCreatorFormExtras } from './creator-form-extras';
import { hydrateReferenceRows } from './reference-hydrate';
import type { RenderBlob } from './primitives';
import { namesFromCatalog, type ReferencedNames } from './helpers';

function referencedNamesOf(value: unknown): ReferencedNames {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
    const out: ReferencedNames = {};
    for (const [id, raw] of Object.entries(value)) {
        if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) continue;
        const entry = raw as Record<string, unknown>;
        if (typeof entry.item_name !== 'string' || typeof entry.item_type !== 'string') continue;
        out[id] = {
            item_name: entry.item_name,
            item_type: entry.item_type,
            ...(typeof entry.pic_url === 'string' ? { pic_url: entry.pic_url } : {}),
            ...(typeof entry.icon === 'string' ? { icon: entry.icon } : {}),
        };
    }
    return out;
}

function renderBlobOf(value: unknown): RenderBlob {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as RenderBlob
        : {};
}

function renderItemListDisplay(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
    equipmentSlotsHtml?: string,
): boolean {
    if (!container) return false;
    try {
        const arr = renderBlobOf(itemArr);
        // Display reads stay outside the live item node and its action-cache catalog.
        const render = (catalog: Record<string, unknown> = {}): string => buildItemListDisplayHtml({
            arr: { ...arr, catalog: { ...arr.catalog, ...catalog } },
            names: { ...referencedNames, ...namesFromCatalog({ catalog }) }, equipmentSlotsHtml,
        });
        const html = render();
        container.innerHTML = html;
        hydrateReferenceRows(container, render);
        // Top gap derived from the blob: the object card stamps _ft_ld_list_top_gap when it rendered a
        // description but NO flavor box (the flavor box's m-3 normally supplies the gap before this list).
        // Apply the margin to the CONTAINER (which has content, so it can't self-collapse like an empty
        // spacer div would); skip when the list rendered nothing so an empty card gets no stray gap.
        const blobGap = !!html && !!(itemArr as { _ft_ld_list_top_gap?: unknown } | null)?._ft_ld_list_top_gap;
        // The TS object card deliberately emits its property mount with mt-4. That cached-card path
        // does not always carry the server-only _ft_ld_list_top_gap hint, so blindly toggling from the
        // hint removed the explicit spacing and jammed the first property list against description text.
        const objectCardGap = !!html && container.classList.contains('ft-oc-property-list');
        container.classList.toggle('mt-4', blobGap || objectCardGap);
        // Spell/menta/curse user-choice pools the blob didn't carry (the cache-first side-info render
        // never does) arrive here as loader rows; fetch + swap them for the real selects. Async and
        // best-effort — the list is already painted and never waits on it.
        try { ftHydrateUserChoiceOptions(container); } catch (e) { /* loader rows just stay put */ }
        return true;
    } catch (e) {
        // e.g. a template.js global (iconMap → checkIfValidUUID) not yet defined at immediate-render
        // time. Return false so the caller can leave the mount un-done and retry on DOMContentLoaded.
        console.error('renderItemListDisplay failed', e);
        return false;
    }
}

// RETIRED 2026-06-09: renderCreatureLists (data-ld-ts-mode="creature") — the long-div item-creature
// card was its only emitter; the unified console renders abilities via the PC-sheet ability_sections
// pane + renderCreatureSummonsLists below. [[project_creature_renderer_unification]]

// Unified creature console Abilities tab (data-ld-ts-mode="creature-summons"): JUST the non-ability
// extra list buckets (shapeshifts / summons / at-will+innate spells / lair+regional). The ability
// blocks come from the shared PC-sheet ability_sections pane in the same tab, so this mount omits
// them to avoid double-rendering.
function renderCreatureSummonsLists(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    try {
        container.innerHTML = buildCreatureSummonsListsHtml({ arr: renderBlobOf(itemArr), names: referencedNames ?? {} });
        // Any spell-category user choice in this mount gets the same async pool fill-in as the
        // main property list.
        try { ftHydrateUserChoiceOptions(container); } catch (e) { /* loader rows just stay put */ }
        return true;
    } catch (e) {
        console.error('renderCreatureSummonsLists failed', e);
        return false;
    }
}

// Static item-creature LAIR tab (data-ld-ts-mode="creature-lair"): JUST the lair + regional-effect
// list blocks. Its own dedicated console tab so the item card visibly advertises that the creature has
// a dedicated lair. Never emitted on the encounter card. If the pane comes back empty (no lair/regional
// resolved), hide the owning tab — same self-heal as the Inventory pane.
function renderCreatureLairLists(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    try {
        const html = buildCreatureLairListsHtml({ arr: renderBlobOf(itemArr), names: referencedNames ?? {} });
        container.innerHTML = html;
        if (html.trim() === '') hideOwningConsoleTab(container);
        return true;
    } catch (e) {
        console.error('renderCreatureLairLists failed', e);
        return false;
    }
}

// Static item-creature MOUNT tab (data-ld-ts-mode="creature-mount"): JUST the mount_creature block.
// Its own dedicated console tab so the item card visibly advertises that the creature is cavalry (it
// auto-spawns riding the referenced mount when added to an encounter). Never emitted on the encounter
// card. If the pane comes back empty (no mount resolved), hide the owning tab — same self-heal as Lair.
function renderCreatureMountLists(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    try {
        const html = buildCreatureMountListsHtml({ arr: renderBlobOf(itemArr), names: referencedNames ?? {} });
        container.innerHTML = html;
        if (html.trim() === '') hideOwningConsoleTab(container);
        return true;
    } catch (e) {
        console.error('renderCreatureMountLists failed', e);
        return false;
    }
}

// Creature item page inventory section (data-ld-ts-mode="inventory"): equipment-slot grid +
// equipped/unequipped lists. Returns false if renderEquipmentSlotGrid isn't ready yet (load order)
// so the DOMContentLoaded re-scan retries.
function renderCreatureInventory(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    if (typeof window.renderEquipmentSlotGrid !== 'function') return false; // grid renderer not loaded yet → retry
    try {
        const html = buildCreatureInventoryHtml({ arr: renderBlobOf(itemArr), names: referencedNames ?? {} });
        container.innerHTML = html;
        // A static item-creature whose intrinsic gear can't fill the paper-doll — no equipped/
        // unequipped inventory, no client-resolvable natural armor/weapon (e.g. a dragon whose
        // natural armor is creature_sub_type-provided and whose natural weapons are leaned to
        // name-only catalog stubs) — renders an EMPTY pane. A blank Inventory tab is bad UX (owner
        // 2026-07-03). The CLIENT is the sole authority on emptiness here: a server-side gate drifts,
        // because PHP layer_entries() carrier-walks that intrinsic gear the cold-cache client never
        // resolves (so the PHP tab-gate green-lights a tab the client then paints blank). Hide the
        // owning console tab from here when the pane actually came back empty. This mount is only ever
        // the static item-creature inventory section (ft_ld_ts_inventory_mount, gated on
        // $ft_static_item_creature), so it can't touch the encounter-creature / PC-sheet console.
        if (html.trim() === '') hideOwningConsoleTab(container);
        return true;
    } catch (e) {
        console.error('renderCreatureInventory failed', e);
        return false;
    }
}

// Hide the Bootstrap nav-pill tab (pill + pane) that OWNS `container`, and — if that pane was the
// active one — promote the first still-visible tab so the console isn't left with a blank body.
// Used when a client-rendered console pane comes back empty (static item-creature Inventory tab with
// no fillable gear). Guarded + scoped: no-op unless the container sits inside a `.tab-pane` whose pill
// we can find, so it can't disturb a non-tabbed mount. Uses class toggles (Bootstrap's own show/active
// model), not a Tab() instance, to avoid a hard dependency on the global `bootstrap`.
function hideOwningConsoleTab(container: HTMLElement): void {
    const pane = container.closest('.tab-pane') as HTMLElement | null;
    if (!pane || !pane.id) return;
    const wasActive = pane.classList.contains('active') || pane.classList.contains('show');
    const btn = document.querySelector('[data-bs-target="#' + pane.id + '"]') as HTMLElement | null;
    const navItem = btn ? (btn.closest('.nav-item') as HTMLElement | null) : null;
    // Hide the pill and deactivate the pane (Bootstrap CSS hides a .tab-pane without .active/.show).
    if (navItem) navItem.style.display = 'none';
    else if (btn) btn.style.display = 'none';
    pane.classList.remove('active', 'show');
    if (btn) { btn.classList.remove('active'); btn.setAttribute('aria-selected', 'false'); }
    if (!wasActive) return;
    // Promote the first tab whose pill is still visible so the console keeps an active pane.
    const nav = navItem ? navItem.parentElement : (btn ? btn.closest('ul, .nav') : null);
    if (!nav) return;
    const links = Array.from(nav.querySelectorAll('.nav-link')) as HTMLElement[];
    for (const link of links) {
        const li = link.closest('.nav-item') as HTMLElement | null;
        if ((li && li.style.display === 'none') || link.style.display === 'none') continue;
        const tgtSel = link.getAttribute('data-bs-target');
        const tgt = tgtSel ? (document.querySelector(tgtSel) as HTMLElement | null) : null;
        if (!tgt) continue;
        link.classList.add('active');
        link.setAttribute('aria-selected', 'true');
        tgt.classList.add('active', 'show');
        break;
    }
}

// Creature SKINNING tab harvest-yield reference (monster_bit + attached_loot_table). Direct-call
// twin of renderCreatureInventory for the client-side static item-creature card's Skinning tab.
function renderCreatureHarvestYields(
    itemArr: unknown,
    referencedNames: ReferencedNames | null | undefined,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    try {
        container.innerHTML = buildCreatureHarvestYieldsHtml({ arr: renderBlobOf(itemArr), names: referencedNames ?? {} });
        return true;
    } catch (e) {
        console.error('renderCreatureHarvestYields failed', e);
        return false;
    }
}

// RETIRED 2026-06-09: renderCreatureNatural (mode "natural") + renderCreatureEnvironment (mode
// "environment") — natural weapons/armor now render in the weapons tab + equipment grid, and
// creature_environment in the Description tab; no PHP emits those modes anymore.

// ── ?ld_ts=1 full-replace mounts ────────────────────────────────────────────
// The PHP combined-list include (ft_ld_ts_render_mount) emits, in place of the property list,
// a <div class="ft-ld-ts-list"> carrying the blob + referenced-names map as
// <script type="application/json"> payloads. We scan for those and render client-side. JSON-in-
// a-script survives innerHTML (so AJAX side panels work); a MutationObserver catches AJAX-injected
// mounts, an initial scan catches the direct item.php page load.
// The render core shared by first-mount (ftMountOne) and in-place re-render (ftLdTsRerender). Paints
// `el` from an ALREADY-PARSED blob (the first innerHTML paint consumes the embedded blob <script>, so
// re-renders read a stash, not the DOM) and, on success, marks done + hydrates scoped child mounts.
function renderLdTsMount(el: HTMLElement, blob: unknown, names: ReferencedNames, mode: string | null, equipHtml: string): boolean {
    let rendered: boolean;
    if (mode === 'creature-summons') {
        rendered = renderCreatureSummonsLists(blob, names, el);
    } else if (mode === 'creature-lair') {
        rendered = renderCreatureLairLists(blob, names, el);
    } else if (mode === 'creature-mount') {
        rendered = renderCreatureMountLists(blob, names, el);
    } else if (mode === 'inventory') {
        rendered = renderCreatureInventory(blob, names, el);
    } else {
        rendered = renderItemListDisplay(blob, names, el, equipHtml);
    }
    // Mark done only on SUCCESS — a failed render (e.g. a template.js global not yet defined at
    // immediate-render time) leaves the mount un-done so the DOMContentLoaded re-scan retries it,
    // by which point every footer script has executed. A successful render is marked, so no re-scan
    // (page-load, DOMContentLoaded, or openItemInfo) re-renders it.
    if (rendered) {
        el.setAttribute('data-ld-ts-done', '1');
        // Hydrate any inline rune chips this render just painted. Scoped to `el` and best-effort:
        // at first paint the rune bundle isn't loaded yet so this no-ops and the footer's
        // DOMContentLoaded ftMountRuneIcons() pass covers it; for AJAX renders it hydrates now.
        if (typeof window.ftMountRuneIcons === 'function') {
            try { window.ftMountRuneIcons(el); } catch (e) { /* footer pass covers init */ }
        }
        // Same for any thumbnail mounts this render painted (scoped, best-effort).
        if (typeof window.ftMountThumbnails === 'function') {
            try { window.ftMountThumbnails(el); } catch (e) { /* footer pass covers init */ }
        }
        // Same for any carousel mounts this render painted (scoped, best-effort).
        if (typeof window.ftMountCarousels === 'function') {
            try { window.ftMountCarousels(el); } catch (e) { /* footer pass covers init */ }
        }
        // Same for any inventory tile mounts this render painted (scoped, best-effort).
        if (typeof window.ftMountInventoryTiles === 'function') {
            try { window.ftMountInventoryTiles(el); } catch (e) { /* footer pass covers init */ }
        }
        // Equipment-grid slot fallback icons (<i data-icon>) — hydrate now (scoped, best-effort);
        // the footer's global ftMountIcons pass covers it on direct page load.
        if (typeof window.ftMountIcons === 'function') {
            try { window.ftMountIcons(el); } catch (e) { /* footer pass covers init */ }
        }
        // Spell/menta/curse user-choice <select>s (renderSpellChoiceSelectRead) need Tom Select.
        // initializeTomSelect is idempotent (skips .tomselected), so this is a safe no-op when the
        // render painted no select; if the global isn't defined yet at first paint, the footer's
        // loadPostFunctions → initializeTomSelect pass (and the AJAX openItemInfo path) covers it.
        if (el.querySelector('select.ts-select:not(.tomselected)') && typeof window.initializeTomSelect === 'function') {
            try { window.initializeTomSelect(); } catch (e) { /* footer pass covers init */ }
        }
    }
    return rendered;
}

// Stash interface: the parsed blob/names/mode/equip kept on the element so ftLdTsRerender can repaint
// without the (now-overwritten) embedded blob <script>.
interface LdTsStash { blob: unknown; names: ReferencedNames; mode: string | null; equipHtml: string; }

function ftMountOne(el: Element): void {
    if (el.getAttribute('data-ld-ts-done') === '1') return;
    const blobEl = el.querySelector('script.ft-ld-ts-blob');
    if (!blobEl) return;
    const namesEl = el.querySelector('script.ft-ld-ts-names');
    try {
        const blob: unknown = JSON.parse(blobEl.textContent || 'null');
        const namesJson: unknown = namesEl ? JSON.parse(namesEl.textContent || '{}') : {};
        const names = referencedNamesOf(namesJson);
        // Equipment-slot grid is now rendered client-side from the blob by the inventory bundle
        // (window.renderEquipmentSlotGrid — inventory→TS Phase 2), not shipped as server HTML.
        const equipHtml = window.renderEquipmentSlotGrid ? window.renderEquipmentSlotGrid(blob) : '';
        // data-ld-ts-mode: "creature" → creature page ability/spell/summon/lair/monster-bit list block;
        // "inventory" → the creature inventory section (equipment-slot grid + equipped/unequipped lists);
        // default → full property list.
        const mode = el.getAttribute('data-ld-ts-mode');
        // Stash the parsed inputs BEFORE the render overwrites el's children (which include the blob
        // <script>) — the in-place toggle re-render reads this stash. Survives even a failed render.
        (el as unknown as { _ftLdStash?: LdTsStash })._ftLdStash = { blob, names, mode, equipHtml };
        renderLdTsMount(el as HTMLElement, blob, names, mode, equipHtml);
    } catch (e) {
        console.error('[ld_ts] mount failed', e);
    }
}
function ftMountListDisplays(): void {
    mountCreatorFormExtras();
    document.querySelectorAll('.ft-ld-ts-list:not([data-ld-ts-done="1"])').forEach(ftMountOne);
}

// Re-render a single mount in place from its stash (the ability list↔icon toggle calls this after
// flipping the cookie). No-op if the element was never mounted (no stash) — falls back to a fresh scan.
function ftLdTsRerender(el: Element): void {
    const stash = (el as unknown as { _ftLdStash?: LdTsStash })._ftLdStash;
    if (!stash) { ftMountListDisplays(); return; }
    try {
        renderLdTsMount(el as HTMLElement, stash.blob, stash.names, stash.mode, stash.equipHtml);
    } catch (e) {
        console.error('[ld_ts] rerender failed', e);
    }
}

// RETIRED 2026-06-09: ftRerenderCreatureAbilities — the scoped list↔icon re-render for the old
// creature-lists mount (.ft-creature-ability-blocks, no longer emitted). The unified console renders
// abilities via the PC-sheet ability_sections pane, which owns its own toggle re-render.

// Window.renderItemListDisplay is declared in globals.d.ts.
Object.assign(window, { renderItemListDisplay, renderCreatureSummonsLists, renderCreatureLairLists, renderCreatureMountLists, renderCreatureInventory, renderCreatureHarvestYields, ftMountListDisplays, ftLdTsRerender });
// window.ftHydrateUserChoiceOptions is registered by ./user-choice-hydrate (imported above) so any
// other bundle that paints list-display markup can fill its loader rows too.

// Render any page-load mount once the DOM is ready. As of the DEFAULT FLIP (2026-05-31) the PHP
// combined-list emits a mount for eligible items with NO flag, so we scan on EVERY page load
// (not just ?ld_ts=1). The scan is a one-time querySelectorAll — a no-op on pages with no mount,
// so it's safe everywhere. AJAX side panels are handled by an explicit ftMountListDisplays() call
// in openItemInfo (template.js) after the panel HTML lands — deliberately NOT a body-wide
// MutationObserver, which on heavy pages (creature sheet + XZ widget + casterCache info panels)
// fires on every mutation and starves the async info-panel rendering.
(function ftInitLdTs(): void {
    // Render IMMEDIATELY on execute: the bundle is a footer <script>, so every PHP mount div above
    // it is already parsed into the DOM — no need to wait for DOMContentLoaded (which only fires
    // after ALL trailing footer scripts finish, adding needless latency to first paint). Keep a
    // DOMContentLoaded listener as a fallback for any mount parsed after this point; the
    // data-ld-ts-done guard makes the re-scan a no-op on already-rendered mounts.
    ftMountListDisplays();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ftMountListDisplays);
})();
